// src/components/EventDetails.js
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import serverURL from "../config";

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClock,
  faUsers,
  faCheckCircle,
  faTrash,
  faFileAlt,
  faChevronDown,
  faEdit,
  faBolt,
} from '@fortawesome/free-solid-svg-icons';
import AssigneeFeedbackList from "../components/AssigneeFeedbackList";
import AssessmentCategoryCards from "../components/AssessmentCategoryCards";
import LessonContent from "../components/LessonContent";
import toast from 'react-hot-toast';

const formatEventLevels = (eventData) => {
  const formattedLevels = (eventData.levels || []).map((level) => {
    const processedCategories = Array.isArray(level.categories)
      ? level.categories
        .filter((category) => category.category_id !== null)
        .map((category) => ({
          id: `saved-${level.id}-${category.category_id}`,
          category_id: category.category_id,
          category_name: category.category_name,
          questions_count: category.question_count,
          duration: category.duration || '30',
          difficulty: category.difficulty || 'Easy',
        }))
      : [];

    return {
      id: level.id,
      level_name: level.level_name,
      level_order: level.level_order,
      type: level.is_online_test ? 'Assessment' : 'Lesson',
      categories: processedCategories,
    };
  });

  return formattedLevels;
};

const EventDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [levels, setLevels] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(1); // Default category ID
  const [questionCount, setQuestionCount] = useState(10); // Default question count
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [lessonTitle, setLessonTitle] = useState('');
  const [lessonContent, setLessonContent] = useState('');
  const [assigneeEmail, setAssigneeEmail] = useState('');
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [isLessonModalOpen, setIsLessonModalOpen] = useState(false);
  const [isAssigneeModalOpen, setIsAssigneeModalOpen] = useState(false);
  const [lessonName, setLessonName] = useState('');
  const [lessonCategory, setLessonCategory] = useState('Technical');
  const [lessonDifficulty, setLessonDifficulty] = useState('Medium');
  const [assigneeName, setAssigneeName] = useState('');
  const [assigneeType, setAssigneeType] = useState('Online');
  const [allCategories, setAllCategories] = useState([]); // All available categories from DB
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false); // Category selection modal
  const [selectedLevelForConfig, setSelectedLevelForConfig] = useState(null); // Level being configured
  const [editingLevelId, setEditingLevelId] = useState(null); // For inline editing
  const [editingLevelName, setEditingLevelName] = useState(''); // For inline editing
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false); // Delete confirmation modal
  const [levelToDelete, setLevelToDelete] = useState(null); // Level being deleted

  const applyEventDetails = useCallback((eventData) => {
    const formattedLevels = formatEventLevels(eventData);

    setEvent(eventData);
    setLevels(formattedLevels);
    setSelectedLevel((currentSelectedLevel) => {
      const currentSelectedLevelId = currentSelectedLevel?.id || null;

      return currentSelectedLevelId
        ? formattedLevels.find((level) => level.id === currentSelectedLevelId) || null
        : formattedLevels.find((level) => level.type === 'Assessment') || null;
    });
  }, []);

  const loadEventDetails = useCallback(async () => {
    const [eventResponse, categoriesResponse] = await Promise.all([
      axios.get(`${serverURL}/events/${id}/details`),
      axios.get(`${serverURL}/events/categories`),
    ]);

    return {
      eventData: eventResponse.data,
      categories: categoriesResponse.data,
    };
  }, [id]);

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      try {
        const { eventData, categories } = await loadEventDetails();
        if (!isMounted) return;

        applyEventDetails(eventData);
        setAllCategories(categories);
      } catch (error) {
        console.error("Failed to fetch event details:", error);
        if (isMounted) {
          toast.error("Failed to load event details");
        }
      }
    };

    void loadData();

    return () => {
      isMounted = false;
    };
  }, [applyEventDetails, loadEventDetails]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);



  const handleDelete = (id) => {
    // Check if it's a saved level (positive ID) or new level (negative ID)
    const isSaved = id > 0;

    if (isSaved) {
      // Show confirmation modal for saved levels
      setLevelToDelete(id);
      setIsDeleteModalOpen(true);
    } else {
      // For unsaved levels, delete directly
      setLevels(levels.filter((level) => level.id !== id));

      // Clear selection if deleted level was selected
      if (selectedLevel?.id === id) {
        setSelectedLevel(null);
      }
    }
  };

  const confirmDelete = async () => {
    if (!levelToDelete) return;

    try {
      await axios.delete(`${serverURL}/events/levels/${levelToDelete}`);
      toast.success('Level deleted successfully');

      // Remove from local state
      setLevels(levels.filter((level) => level.id !== levelToDelete));

      // Clear selection if deleted level was selected
      if (selectedLevel?.id === levelToDelete) {
        setSelectedLevel(null);
      }

      // Close modal and reset
      setIsDeleteModalOpen(false);
      setLevelToDelete(null);
    } catch (error) {
      console.error('Error deleting level:', error);
      toast.error('Failed to delete level from database');
    }
  };

  const cancelDelete = () => {
    setIsDeleteModalOpen(false);
    setLevelToDelete(null);
  };

  const addLevel = async (level) => {
    const newLevels = [...levels, level];
    setLevels(newLevels);
    // Auto-save after adding level
    await saveLevelsToBackend(newLevels);
  };

  const closeCategoryConfigModal = () => {
    setIsCategoryModalOpen(false);
    setSelectedLevelForConfig(null);
  };

  // Auto-save function to save levels to backend
  const saveLevelsToBackend = async (levelsToSave = null) => {
    try {
      const levelsData = levelsToSave || levels;

      // Transform levels array to match backend format
      const formattedLevels = levelsData.map((level, index) => {
        const baseLevel = {
          id: level.id,
          level_name: level.level_name,
          level_order: index + 1,
          is_online_test: level.type === 'Assessment'
        };

        if (level.type === 'Assessment' && level.categories?.length > 0) {
          baseLevel.categories = level.categories.map(cat => ({
            category_id: cat.category_id,
            question_count: parseInt(cat.questions_count) || 0,
            duration: parseInt(cat.duration) || 30,
            difficulty: cat.difficulty || 'Easy'
          }));
        }

        return baseLevel;
      });

      const response = await axios.post(
        `${serverURL}/events/${event.id}/configure-levels`,
        { levels: formattedLevels },
        {
          headers: {
            "Content-Type": "application/json"
          }
        }
      );

      if (response.data.success) {
        toast.success("Changes saved successfully!");
        const refreshedEvent = await axios.get(`${serverURL}/events/${id}/details`);
        applyEventDetails(refreshedEvent.data);
        return true;
      } else {
        toast.error(response.data.message || "Failed to save changes");
        return false;
      }
    } catch (error) {
      console.error("Error saving levels:", error);
      const errorMsg = error.response?.data?.message || "Failed to save changes";
      toast.error(errorMsg);
      return false;
    }
  };

  const handleLessonSubmit = () => {
    const newLevel = {
      id: -(levels.length + 1), // Negative ID for new level
      level_name: lessonName || `Lesson ${levels.length + 1}`,
      type: 'Lesson',
      category: lessonCategory,
      difficulty: lessonDifficulty,
    };
    addLevel(newLevel);
    closeLessonModal();
  };

  const handleAssigneeSubmit = () => {
    const newLevel = {
      id: -(levels.length + 1), // Negative ID for new level
      level_name: assigneeName || `Feedback ${levels.length + 1}`,
      type: 'Feedback',
      assigneeType: assigneeType,
    };
    addLevel(newLevel);
    closeAssigneeModal();
  };



  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleDropdownSelect = async (type) => {
    setIsDropdownOpen(false);
    // For Lesson and Feedback, open the detail modal before creating the level
    if (type === 'Lesson') {
      openLessonModal();
      return;
    }
    if (type === 'Feedback') {
      openAssigneeModal();
      return;
    }
    // For Assessment, add directly with a unique negative ID
    const newId = -(levels.length + 1);
    const newLevel = {
      id: newId,
      level_name: `Level ${levels.length + 1}`,
      type: type,
    };
    const newLevels = [...levels, newLevel];
    setLevels(newLevels);
    // Auto-save after adding via dropdown
    await saveLevelsToBackend(newLevels);
  };

  const handleLevelClick = (level) => {
    setSelectedLevel(level);
  };

  const handleCategoriesChange = async (updatedCategories) => {
    if (!selectedLevel) return;

    // Update the categories in the levels array
    const updatedLevels = levels.map(level => {
      if (level.id === selectedLevel.id) {
        return {
          ...level,
          categories: updatedCategories
        };
      }
      return level;
    });

    setLevels(updatedLevels);

    // Also update the selectedLevel to keep UI in sync
    setSelectedLevel({
      ...selectedLevel,
      categories: updatedCategories
    });

    // Auto-save after category change
    await saveLevelsToBackend(updatedLevels);
  };

  const handleEditLevelName = (level) => {
    setEditingLevelId(level.id);
    setEditingLevelName(level.level_name);
  };

  const handleSaveLevelName = async () => {
    if (!editingLevelName.trim()) {
      toast.error('Level name cannot be empty');
      return;
    }

    const updatedLevels = levels.map(level =>
      level.id === editingLevelId
        ? { ...level, level_name: editingLevelName }
        : level
    );
    setLevels(updatedLevels);

    if (selectedLevel?.id === editingLevelId) {
      setSelectedLevel({ ...selectedLevel, level_name: editingLevelName });
    }

    // Auto-save after name change
    const saved = await saveLevelsToBackend(updatedLevels);

    if (saved) {
      setEditingLevelId(null);
      setEditingLevelName('');
      toast.success('Level name updated');
    }
  };

  const handleCancelEditLevelName = () => {
    setEditingLevelId(null);
    setEditingLevelName('');
  };

  // Level type → visual config
  const levelTypeConfig = {
    Assessment: { accent: 'border-campus-primary', badge: 'bg-red-50 text-campus-primary', icon: faCheckCircle },
    Lesson: { accent: 'border-blue-400', badge: 'bg-blue-50 text-blue-600', icon: faFileAlt },
    Feedback: { accent: 'border-purple-400', badge: 'bg-purple-50 text-purple-600', icon: faUsers },
  };

  const renderLevels = () => (
    <nav className="space-y-1.5" aria-label="Event levels">
      {levels.map((level, idx) => {
        const isActive = selectedLevel?.id === level.id;
        const isEditing = editingLevelId === level.id;
        const cfg = levelTypeConfig[level.type] || levelTypeConfig.Assessment;

        return (
          <button
            key={level.id}
            type="button"
            className={`group relative flex items-center gap-3 pl-4 pr-3 py-3 rounded-xl border-l-4 transition-all duration-200 cursor-pointer w-full text-left
              ${isActive
                ? `${cfg.accent} bg-campus-surface shadow-card`
                : 'border-transparent bg-campus-surface hover:bg-campus-bg hover:border-campus-border'
              }`}
            onClick={() => !isEditing && handleLevelClick(level)}
          >
            {/* Step indicator */}
            <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border-2
              ${isActive ? 'border-campus-primary bg-campus-primary text-white' : 'border-campus-border bg-campus-bg text-campus-text-body'}`}>
              {idx + 1}
            </div>

            {/* Name + badge */}
            <div className="flex-1 min-w-0">
              {isEditing ? (
                <div className="flex items-center gap-2">
                  <label htmlFor={`level-name-input-${level.id}`} className="sr-only">Level name</label>
                  <input
                    id={`level-name-input-${level.id}`}
                    value={editingLevelName}
                    onChange={(e) => setEditingLevelName(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSaveLevelName()}
                    onKeyDown={(e) => e.key === 'Escape' && handleCancelEditLevelName()}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                    className="flex-1 border border-campus-primary px-2 py-0.5 text-sm rounded focus:outline-none bg-campus-surface"
                  />
                  <button onClick={(e) => { e.stopPropagation(); handleSaveLevelName(); }} className="text-campus-success hover:opacity-80 font-bold text-base" title="Save">✓</button>
                  <button onClick={(e) => { e.stopPropagation(); handleCancelEditLevelName(); }} className="text-campus-error hover:opacity-80 font-bold text-base" title="Cancel">✕</button>
                </div>
              ) : (
                <>
                  <p className={`text-sm font-semibold truncate ${isActive ? 'text-campus-text-primary' : 'text-campus-text-body'}`}>
                    {level.level_name}
                  </p>
                  <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full mt-0.5 ${cfg.badge}`}>
                    <FontAwesomeIcon icon={cfg.icon} className="text-[10px]" />
                    {level.type}
                  </span>
                </>
              )}
            </div>

            {/* Actions: edit + delete (visible on hover) */}
            {!isEditing && (
              <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleEditLevelName(level); }}
                  className="p-1 rounded hover:bg-campus-bg text-campus-text-body hover:text-campus-primary transition-colors"
                  title="Rename"
                >
                  <FontAwesomeIcon icon={faEdit} className="text-xs" />
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleDelete(level.id); }}
                  className="p-1 rounded hover:bg-red-50 text-campus-text-body hover:text-campus-error transition-colors"
                  aria-label="Delete Level"
                >
                  <FontAwesomeIcon icon={faTrash} className="text-xs" />
                </button>
              </div>
            )}
          </button>
        );
      })}
    </nav>
  );

  const renderSelectedLevelDetails = () => {
    if (!selectedLevel) {
      return (
        <div className="flex items-center justify-center h-full">
          <p className="text-gray-500">Select a level to view details</p>
        </div>
      );
    }

    switch (selectedLevel.type) {
      case 'Feedback':
        return <AssigneeFeedbackList eventId={id} levelId={selectedLevel?.id} />;
      case 'Assessment':
        return (
          <div>

            <AssessmentCategoryCards
              key={selectedLevel?.id}
              selectedLevel={selectedLevel}
              allCategories={allCategories}
              onCategoriesChange={handleCategoriesChange}
            />
          </div>
        );
      case 'Lesson':
        return <LessonContent eventId={id} levelId={selectedLevel?.id} />;
      default:
        return (
          <div className="flex items-center justify-center h-full">
            <p className="text-gray-500">Select a level to view details</p>
          </div>
        );
    }
  };


  const renderConfigurationModal = () => {
    if (!isConfigModalOpen || !selectedLevel) return null;

    const getModalContent = () => {
      switch (selectedLevel.type) {
        case 'Assessment':
          return (
            <div className="space-y-4">
              <div>
                <label htmlFor="config-category" className="block mb-2 font-semibold text-campus-text-primary">Category Name:</label>
                <select
                  id="config-category"
                  className="w-full p-2.5 border border-campus-border rounded-lg focus:outline-none focus:ring-2 focus:ring-campus-primary bg-campus-bg text-campus-text-primary"
                  value={selectedCategoryId}
                  onChange={(e) => setSelectedCategoryId(e.target.value)}
                >
                  <option value="">-- Select a Category --</option>
                  {allCategories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.category_name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="config-question-count" className="block mb-2 font-semibold text-campus-text-primary">Number of Questions:</label>
                <input
                  id="config-question-count"
                  type="number"
                  min="1"
                  max="100"
                  className="w-full p-2.5 border border-campus-border rounded-lg focus:outline-none focus:ring-2 focus:ring-campus-primary bg-campus-bg text-campus-text-primary"
                  value={questionCount}
                  onChange={(e) => setQuestionCount(e.target.value)}
                  placeholder="Enter number of questions (1-100)"
                />
              </div>
            </div>
          );

        case 'Lesson':
          return (
            <div className="space-y-4">
              <div>
                <label htmlFor="config-lesson-title" className="block mb-2">Lesson Title:</label>
                <input
                  id="config-lesson-title"
                  type="text"
                  className="w-full p-2 border rounded-md"
                  value={lessonTitle}
                  onChange={(e) => setLessonTitle(e.target.value)}
                  placeholder="Enter lesson title"
                />
              </div>
              <div>
                <label htmlFor="config-lesson-content" className="block mb-2">Content:</label>
                <textarea
                  id="config-lesson-content"
                  className="w-full p-2 border rounded-md h-32"
                  value={lessonContent}
                  onChange={(e) => setLessonContent(e.target.value)}
                  placeholder="Enter lesson content"
                ></textarea>
              </div>
            </div>
          );

        case 'Feedback':
          return (
            <div className="space-y-4">
              <div>
                <label htmlFor="config-assignee-name" className="block mb-2">Assignee Name:</label>
                <input
                  id="config-assignee-name"
                  type="text"
                  className="w-full p-2 border rounded-md"
                  value={assigneeName}
                  onChange={(e) => setAssigneeName(e.target.value)}
                  placeholder="Enter assignee name"
                />
              </div>
              <div>
                <label htmlFor="config-assignee-email" className="block mb-2">Email:</label>
                <input
                  id="config-assignee-email"
                  type="email"
                  className="w-full p-2 border rounded-md"
                  value={assigneeEmail}
                  onChange={(e) => setAssigneeEmail(e.target.value)}
                  placeholder="Enter email address"
                />
              </div>
            </div>
          );

        default:
          return null;
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" style={{ zIndex: 9999 }}>
        <div className="bg-white rounded-lg p-6 w-full max-w-md">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold">
              Configure {selectedLevel.type}
            </h3>
            <button
              onClick={closeConfigModal}
              className="text-gray-500 hover:text-gray-700"
            >
              ✖
            </button>
          </div>
          {getModalContent()}
          <div className="flex justify-end mt-6 space-x-2">
            <button
              onClick={closeConfigModal}
              className="px-4 py-2 text-campus-text-body bg-campus-surface border border-campus-border rounded-lg hover:bg-campus-bg transition font-medium focus:outline-none focus:ring-2 focus:ring-campus-primary"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                // Handle save based on type
                closeConfigModal();
              }}
              className="px-4 py-2 bg-campus-primary text-white rounded-lg hover:bg-campus-primary-dark transition font-medium focus:outline-none focus:ring-2 focus:ring-campus-primary"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (!event) {
    return <div className="text-center">Event details not found.</div>;
  }
  const closeConfigModal = () => setIsConfigModalOpen(false);

  const openLessonModal = () => setIsLessonModalOpen(true);
  const closeLessonModal = () => {
    setIsLessonModalOpen(false);
    setLessonName('');
    setLessonCategory('Technical');
    setLessonDifficulty('Medium');
  };

  const openAssigneeModal = () => setIsAssigneeModalOpen(true);
  const closeAssigneeModal = () => {
    setIsAssigneeModalOpen(false);
    setAssigneeName('');
    setAssigneeType('Online');
  };

  const renderLessonModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" style={{ zIndex: 9999 }}>
      <div className="bg-campus-surface rounded-xl shadow-soft p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-heading font-bold text-campus-text-primary">Add Lesson</h3>
          <button onClick={closeLessonModal} className="text-campus-text-body hover:text-campus-text-primary text-xl leading-none">×</button>
        </div>
        <div className="space-y-4">
          <div>
            <label htmlFor="lesson-modal-name" className="block mb-1.5 text-sm font-medium text-campus-text-primary">Lesson Name:</label>
            <input
              id="lesson-modal-name"
              type="text"
              className="w-full p-2.5 border border-campus-border rounded-lg focus:outline-none focus:ring-2 focus:ring-campus-primary bg-campus-bg text-campus-text-primary placeholder-campus-text-body"
              value={lessonName}
              onChange={(e) => setLessonName(e.target.value)}
              placeholder="Enter lesson name"
            />
          </div>
          <div>
            <label htmlFor="lesson-modal-category" className="block mb-1.5 text-sm font-medium text-campus-text-primary">Category:</label>
            <select
              id="lesson-modal-category"
              className="w-full p-2.5 border border-campus-border rounded-lg focus:outline-none focus:ring-2 focus:ring-campus-primary bg-campus-bg text-campus-text-primary"
              value={lessonCategory}
              onChange={(e) => setLessonCategory(e.target.value)}
            >
              <option value="Technical">Technical</option>
              <option value="Aptitude">Aptitude</option>
            </select>
          </div>
          <div>
            <label htmlFor="lesson-modal-difficulty" className="block mb-1.5 text-sm font-medium text-campus-text-primary">Difficulty:</label>
            <select
              id="lesson-modal-difficulty"
              className="w-full p-2.5 border border-campus-border rounded-lg focus:outline-none focus:ring-2 focus:ring-campus-primary bg-campus-bg text-campus-text-primary"
              value={lessonDifficulty}
              onChange={(e) => setLessonDifficulty(e.target.value)}
            >
              <option value="Easy">Easy</option>
              <option value="Medium">Medium</option>
              <option value="Hard">Hard</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end mt-6 space-x-2">
          <button onClick={closeLessonModal} className="px-4 py-2 text-campus-text-body bg-campus-surface border border-campus-border rounded-lg hover:bg-campus-bg transition font-medium">Cancel</button>
          <button onClick={handleLessonSubmit} className="px-4 py-2 bg-campus-primary text-white rounded-lg hover:bg-campus-primary-dark transition font-medium">Save</button>
        </div>
      </div>
    </div>
  );

  const renderAssigneeModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" style={{ zIndex: 9999 }}>
      <div className="bg-campus-surface rounded-xl shadow-soft p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-heading font-bold text-campus-text-primary">Add Assignee</h3>
          <button onClick={closeAssigneeModal} className="text-campus-text-body hover:text-campus-text-primary text-xl leading-none">×</button>
        </div>
        <div className="space-y-4">
          <div>
            <label htmlFor="assignee-modal-name" className="block mb-1.5 text-sm font-medium text-campus-text-primary">Assignee Name:</label>
            <input
              id="assignee-modal-name"
              type="text"
              className="w-full p-2.5 border border-campus-border rounded-lg focus:outline-none focus:ring-2 focus:ring-campus-primary bg-campus-bg text-campus-text-primary placeholder-campus-text-body"
              value={assigneeName}
              onChange={(e) => setAssigneeName(e.target.value)}
              placeholder="Enter assignee name"
            />
          </div>
          <div>
            <label htmlFor="assignee-modal-type" className="block mb-1.5 text-sm font-medium text-campus-text-primary">Type:</label>
            <select
              id="assignee-modal-type"
              className="w-full p-2.5 border border-campus-border rounded-lg focus:outline-none focus:ring-2 focus:ring-campus-primary bg-campus-bg text-campus-text-primary"
              value={assigneeType}
              onChange={(e) => setAssigneeType(e.target.value)}
            >
              <option value="Online">Online</option>
              <option value="Offline">Offline</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end mt-6 space-x-2">
          <button onClick={closeAssigneeModal} className="px-4 py-2 text-campus-text-body bg-campus-surface border border-campus-border rounded-lg hover:bg-campus-bg transition font-medium">Cancel</button>
          <button onClick={handleAssigneeSubmit} className="px-4 py-2 bg-campus-primary text-white rounded-lg hover:bg-campus-primary-dark transition font-medium">Save</button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col min-h-screen bg-campus-bg">

      <main className="flex-grow p-4 sm:p-6">
        <div className="bg-campus-surface shadow-card rounded-card">
          {/* ── KPI Stats Row (4 cards) ───────────────────────────────── */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 p-5 pb-0">
            {/* Duration */}
            <div className="bg-campus-surface border border-campus-border border-l-4 border-l-blue-400 rounded-xl p-4 flex items-center gap-3 shadow-sm">
              <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                <FontAwesomeIcon icon={faClock} className="text-blue-500 text-sm" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-campus-text-body font-medium uppercase tracking-wide">Duration</p>
                <p className="text-sm font-bold text-campus-text-primary truncate">{event.duration} min</p>
              </div>
            </div>
            {/* Candidates count */}
            <div className="bg-campus-surface border border-campus-border border-l-4 border-l-amber-400 rounded-xl p-4 flex items-center gap-3 shadow-sm">
              <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                <FontAwesomeIcon icon={faUsers} className="text-amber-500 text-sm" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-campus-text-body font-medium uppercase tracking-wide">Candidates</p>
                <p className="text-sm font-bold text-campus-text-primary truncate">{event.candidate_count}</p>
              </div>
            </div>
            {/* Pass % */}
            <div className="bg-campus-surface border border-campus-border border-l-4 border-l-purple-400 rounded-xl p-4 flex items-center gap-3 shadow-sm">
              <div className="w-9 h-9 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
                <FontAwesomeIcon icon={faCheckCircle} className="text-purple-500 text-sm" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-campus-text-body font-medium uppercase tracking-wide">Pass %</p>
                <p className="text-sm font-bold text-campus-text-primary truncate">{event.pass_percentage}%</p>
              </div>
            </div>
            {/* Status */}
            <div className={`bg-campus-surface border border-campus-border border-l-4 rounded-xl p-4 flex items-center gap-3 shadow-sm ${event.status === 'upcoming' ? 'border-l-green-400' : 'border-l-campus-error'
              }`}>
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${event.status === 'upcoming' ? 'bg-green-50' : 'bg-red-50'
                }`}>
                <FontAwesomeIcon icon={faBolt} className={`text-sm ${event.status === 'upcoming' ? 'text-green-500' : 'text-campus-error'}`} />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-campus-text-body font-medium uppercase tracking-wide">Status</p>
                <p className={`text-sm font-bold capitalize truncate ${event.status === 'upcoming' ? 'text-green-600' : 'text-campus-error'}`}>
                  {event.status}
                </p>
              </div>
            </div>
          </div>

          {/* ── Heading + Action Buttons ───────────────────────────── */}
          <div className="p-5 pt-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div>
              <h1 className="text-2xl sm:text-3xl font-heading font-bold text-campus-text-primary leading-tight">{event.name}</h1>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
                <span className="text-xs text-campus-text-body">
                  <span className="font-semibold text-campus-text-primary">{event.institution}</span>
                </span>
                <span className="text-campus-border text-xs hidden sm:inline">|</span>
                <span className="text-xs text-campus-text-body">
                  {new Date(event.event_date).toLocaleString()}
                </span>
              </div>
            </div>
            {/* Action buttons */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => navigate(`/candidates/${event.id}`)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg
                  bg-campus-primary hover:bg-campus-primary-dark text-white
                  shadow-sm hover:shadow-md active:scale-[0.98]
                  transition-all duration-200"
              >
                <FontAwesomeIcon icon={faUsers} className="text-xs" />
                Candidates
              </button>
              <button
                onClick={() => navigate(`/consolidated/${event.id}`)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg
                  border border-campus-border text-campus-text-primary bg-campus-surface
                  hover:border-campus-primary hover:text-campus-primary hover:bg-campus-bg
                  active:scale-[0.98]
                  transition-all duration-200"
              >
                <FontAwesomeIcon icon={faFileAlt} className="text-xs" />
                Report
              </button>
            </div>
          </div>

          {/* ── Main Content Area ─────────────────────────────────────────── */}
          <div className="flex flex-col lg:flex-row">
            {/* Aside: Level stepper */}
            <div className="lg:w-1/3 p-4 bg-campus-bg lg:border-r border-campus-border flex flex-col gap-3">
              <div className="flex justify-between items-center mb-1">
                <h2 className="text-base font-heading font-bold text-campus-text-primary">Event Levels</h2>
              </div>
              {renderLevels()}

              {/* Add Level — pinned at the bottom of the sidebar */}
              <div className="pt-2 mt-auto border-t border-campus-border" ref={dropdownRef}>
                <div className="relative">
                  <button
                    onClick={toggleDropdown}
                    className="w-full bg-campus-primary hover:bg-campus-primary-dark rounded-lg text-white px-4 py-2.5 flex items-center justify-center gap-2 font-medium transition-all duration-200 text-sm shadow-sm hover:shadow-md active:scale-[0.98]"
                  >
                    <span>+ Add Level</span>
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      className={`text-xs transition-transform duration-300 ${isDropdownOpen ? 'rotate-180' : 'rotate-0'
                        }`}
                    />
                  </button>

                  {/* Animated dropdown — always mounted, visibility driven by classes */}
                  <div
                    className={`absolute bottom-full left-0 right-0 mb-2 bg-campus-surface border border-campus-border rounded-xl shadow-hover z-50 overflow-hidden
                      transition-all duration-200 ease-out origin-bottom
                      ${isDropdownOpen
                        ? 'opacity-100 scale-y-100 translate-y-0 pointer-events-auto'
                        : 'opacity-0 scale-y-95 translate-y-1 pointer-events-none'
                      }`}
                  >
                    <button
                      onClick={() => handleDropdownSelect('Lesson')}
                      className="block w-full text-left px-4 py-3 hover:bg-campus-bg text-campus-text-body hover:text-campus-text-primary transition-colors duration-150 text-sm font-medium rounded-t-xl"
                    >
                      Module
                    </button>
                    <div className="h-px bg-campus-border mx-3" />
                    <button
                      onClick={() => handleDropdownSelect('Assessment')}
                      className="block w-full text-left px-4 py-3 hover:bg-campus-bg text-campus-text-body hover:text-campus-text-primary transition-colors duration-150 text-sm font-medium"
                    >
                      Assessment
                    </button>
                    <div className="h-px bg-campus-border mx-3" />
                    <button
                      onClick={() => handleDropdownSelect('Feedback')}
                      className="block w-full text-left px-4 py-3 hover:bg-campus-bg text-campus-text-body hover:text-campus-text-primary transition-colors duration-150 text-sm font-medium rounded-b-xl"
                    >
                      Feedback
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Selected Level Details Panel */}
            <div className="flex-grow">
              {renderSelectedLevelDetails()}
            </div>
          </div>
        </div>

      </main>
      {isLessonModalOpen && renderLessonModal()}
      {isConfigModalOpen && renderConfigurationModal()}
      {isAssigneeModalOpen && renderAssigneeModal()}

      {/* Category Configuration Confirmation Modal */}
      {
        isCategoryModalOpen && selectedLevelForConfig && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-campus-surface rounded-2xl shadow-soft max-w-2xl w-full mx-4">
              <div className="p-6 border-b border-campus-border">
                <h2 className="text-2xl font-heading font-bold text-campus-text-primary">
                  Configuration Details - {selectedLevelForConfig.level_name}
                </h2>
                <p className="text-campus-text-body mt-1 text-sm">
                  Review the configured categories for this Assessment level
                </p>
              </div>

              <div className="p-6 max-h-96 overflow-y-auto">
                {selectedLevelForConfig.categories && selectedLevelForConfig.categories.length > 0 ? (
                  <div className="space-y-3">
                    {selectedLevelForConfig.categories.map((category, index) => (
                      <div key={`${category.id}-${index}`} className="border border-campus-border rounded-xl p-4 bg-campus-bg hover:bg-campus-surface transition-colors duration-150">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-bold text-campus-text-primary">{category.category_name}</h3>
                            <p className="text-campus-text-body text-sm mt-1">
                              Questions: <span className="font-semibold text-blue-600">{category.questions_count}</span>
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-campus-text-body text-sm">Duration: <span className="font-semibold">{category.duration} mins</span></p>
                            {(() => {
                              let difficultyClass = 'bg-red-500';
                              if (category.difficulty === 'Easy') difficultyClass = 'bg-green-500';
                              else if (category.difficulty === 'Medium') difficultyClass = 'bg-yellow-500';
                              return (
                                <span className={`inline-block mt-1 px-2 py-1 text-xs font-semibold text-white rounded ${difficultyClass}`}>
                                  {category.difficulty}
                                </span>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center justify-center py-8 text-gray-500">
                    <p>No categories configured yet</p>
                  </div>
                )}
              </div>

              <div className="p-6 border-t border-campus-border flex justify-end gap-3">
                <button
                  onClick={closeCategoryConfigModal}
                  className="px-6 py-2 border border-campus-border rounded-lg text-campus-text-body hover:bg-campus-bg transition font-semibold"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Delete Confirmation Modal */}
      {
        isDeleteModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" style={{ zIndex: 9999 }}>
            <div className="bg-campus-surface rounded-2xl shadow-soft max-w-md w-full mx-4">
              <div className="p-6 border-b border-campus-border">
                <h2 className="text-2xl font-heading font-bold text-campus-text-primary">Delete Level</h2>
                <p className="text-campus-text-body mt-2">
                  Are you sure you want to delete this level?
                </p>
              </div>

              <div className="p-6 border-t border-campus-border flex justify-end gap-3">
                <button
                  onClick={cancelDelete}
                  className="px-6 py-2 border border-campus-border rounded-lg text-campus-text-body hover:bg-campus-bg transition font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-6 py-2 bg-campus-primary text-white rounded-lg hover:bg-campus-primary-dark transition font-semibold flex items-center gap-2"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )
      }



    </div >
  );
};

export default EventDetails;
